export type Theme = {
  name: string
  palette: Record<number, string>
}

const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h *= 60
  }

  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) }
}

const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100
  l /= 100
  const k = (n: number) => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) =>
    Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))))
      .toString(16)
      .padStart(2, '0')

  return `#${f(0)}${f(8)}${f(4)}`
}

export const generatePalette = (name: string, baseHex: string): Theme => {
  const { h: baseH, s: baseS, l: baseL } = hexToHsl(baseHex)

  const lightnessMap: Record<number, number> = {
    50: Math.min(baseL + 58, 98),
    100: Math.min(baseL + 50, 96),
    200: Math.min(baseL + 40, 89),
    300: Math.min(baseL + 30, 85),
    400: Math.min(baseL + 10, 75),
    500: baseL,
    600: Math.max(baseL - 7, 8),
    700: Math.max(baseL - 12, 8),
    800: Math.max(baseL - 18, 8),
    900: Math.max(baseL - 22, 8),
    950: Math.max(baseL - 26, 8)
  }

  const adjustSaturation = (step: number): number => {
    if (step <= 300) return Math.max(baseS - 30, 30)
    if (step == 400) return Math.max(baseS - 5, 30)
    if (step >= 800) return Math.max(baseS - 20, 35)
    return baseS // 중간 단계는 유지
  }

  const adjustHue = (h: number): number => {
    // 청록색 계열은 약간만 옐로우 쪽으로 밀기
    if (h >= 160 && h <= 190) return h - 5
    return h
  }

  const palette: Record<number, string> = {}

  for (const stepStr of Object.keys(lightnessMap)) {
    const step = Number(stepStr)
    const lightness = lightnessMap[step]
    const adjustedS = adjustSaturation(step)
    const adjustedH = adjustHue(baseH)
    palette[step] = step === 500 ? baseHex : hslToHex(adjustedH, adjustedS, lightness)
  }

  return { name, palette }
}

export const primaries: Theme[] = [generatePalette('default', '#F05223')]

export const getPrimary = (name: string) => primaries.find((p) => p.name === name)
